import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Users, 
  Zap, 
  Wifi, 
  Car,
  MapPin,
  Droplets,
  Flame,
  TreePine,
  PawPrint,
  Utensils,
  Sun,
  Sunset,
  Sunrise,
  Building2,
  Waves,
  Circle
} from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import AmenityItem from './components/AmenityItem';
import BookingTooltip from './components/BookingTooltip';
import { useSiteBookings } from '@/hooks/useSiteBookings';
import { useSiteBookingStatus } from '@/hooks/useSiteBookingStatus';

export interface CampSite {
  id: string;
  name: string;
  location: string;
  price: number;
  image: string;
  capacity: number;
  amenities: {
    electric?: boolean;
    amperage?: number;
    electric50Amp?: boolean;
    water?: boolean;
    sewage?: boolean;
    maxRVLength?: number;
    wifi?: boolean;
    petsAllowed?: boolean;
    backIn?: boolean;
    pullThru?: boolean;
    nearLake?: boolean;
    firePit?: boolean;
    picnicTable?: boolean;
    gravelSurface?: boolean;
    paved?: boolean;
    shade?: boolean;
    shadeMorning?: boolean;
    shadeAfternoon?: boolean;
    grill?: boolean;
    nearRestroom?: boolean;
  };
  available?: boolean;
  status?: 'active' | 'coming_soon' | 'maintenance' | 'seasonal_closure' | 'inactive';
  statusMessage?: string | null;
}

export interface CampSiteCardProps {
  site: CampSite;
  onBook: (siteId: string) => void;
  startDate?: Date;
  endDate?: Date;
}

const CampSiteCard: React.FC<CampSiteCardProps> = ({ 
  site, 
  onBook,
  startDate,
  endDate
}) => {
  const [realTimeAvailable, setRealTimeAvailable] = useState(site.available);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const { bookings } = useSiteBookings(site.id);
  
  // Get current booking status for always-visible status indicator
  const { bookedNow, currentBooking, isBookedForDateRange } = useSiteBookingStatus(
    bookings, 
    startDate && endDate ? { start: startDate, end: endDate } : undefined
  );
  
  // Real-time availability check with improved error handling
  const checkRealTimeAvailability = async (retryCount = 0) => {
    if (!startDate || !endDate) {
      setRealTimeAvailable(site.available);
      return;
    }

    setIsCheckingAvailability(true);

    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      const { data: isAvailable, error } = await supabase
        .rpc('check_site_availability_for_dates', {
          site_id: site.id,
          check_start_date: formattedStartDate,
          check_end_date: formattedEndDate
        });
      
      if (error) {
        console.error('Error checking real-time availability:', error);
        
        // Retry once if it's a temporary error
        if (retryCount === 0 && error.message?.includes('connection')) {
          setTimeout(() => checkRealTimeAvailability(1), 1000);
          return;
        }
        
        // Fall back to initial availability state
        setRealTimeAvailable(site.available);
      } else {
        setRealTimeAvailable(!!isAvailable);
      }
    } catch (error) {
      console.error('Error in real-time availability check:', error);
      
      // Retry once for network errors
      if (retryCount === 0) {
        setTimeout(() => checkRealTimeAvailability(1), 1000);
        return;
      }
      
      // Fall back to initial availability state
      setRealTimeAvailable(site.available);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Check availability when dates change
  useEffect(() => {
    checkRealTimeAvailability();
  }, [startDate, endDate, site.id]);

  // Listen for real-time updates to this specific site
  useEffect(() => {
    if (!site.id) return;

    const channel = supabase
      .channel(`site-${site.id}-updates`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_bookings',
          filter: `site_id=eq.${site.id}`
        },
        (payload) => {
          console.log('Site booking update detected:', payload);
          // Small delay to ensure database consistency
          setTimeout(() => checkRealTimeAvailability(), 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'camping_sites',
          filter: `id=eq.${site.id}`
        },
        (payload) => {
          console.log('Site data update detected:', payload);
          // Small delay to ensure database consistency
          setTimeout(() => checkRealTimeAvailability(), 500);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [site.id]);

  // Determine which availability to display
  const displayAvailable = startDate && endDate ? realTimeAvailable : !bookedNow;
  
  // Current status for always-visible indicator
  const currentStatus = bookedNow ? 'Currently Booked' : 'Available Tonight';
  const currentStatusColor = bookedNow ? 'text-red-600' : 'text-green-600';
  const statusDotColor = bookedNow ? 'text-red-500' : 'text-green-500';

  const renderAmenities = () => {
    const amenities = [];

    // Pets allowed
    if (site.amenities.petsAllowed) {
      amenities.push(
        <AmenityItem key="pets" icon={PawPrint} text="Pets allowed" />
      );
    }

    // Electric service
    if (site.amenities.electric50Amp) {
      amenities.push(
        <AmenityItem key="electric-50" icon={Zap} text="Electric 30/50AMP" />
      );
    } else if (site.amenities.amperage) {
      amenities.push(
        <AmenityItem key="electric-amp" icon={Zap} text={`Electric ${site.amenities.amperage}AMP`} />
      );
    } else if (site.amenities.electric) {
      amenities.push(
        <AmenityItem key="electric" icon={Zap} text="Electric" />
      );
    }

    // Water hookup
    if (site.amenities.water) {
      amenities.push(
        <AmenityItem key="water" icon={Droplets} text="Water hookup" />
      );
    }

    // Sewage hookup
    if (site.amenities.sewage) {
      amenities.push(
        <AmenityItem key="sewage" icon={Building2} text="Sewage hookup" />
      );
    } else {
      amenities.push(
        <AmenityItem key="no-sewage" icon={Building2} text="No sewage hookup" />
      );
    }

    // WiFi
    if (site.amenities.wifi) {
      amenities.push(
        <AmenityItem key="wifi" icon={Wifi} text="WiFi" />
      );
    } else {
      amenities.push(
        <AmenityItem key="no-wifi" icon={Wifi} text="No WiFi" />
      );
    }

    // Site type
    if (site.amenities.backIn && site.amenities.pullThru) {
      amenities.push(
        <AmenityItem key="site-type" icon={MapPin} text="Back-in & Pull-thru site" />
      );
    } else if (site.amenities.backIn) {
      amenities.push(
        <AmenityItem key="site-type" icon={MapPin} text="Back-in site" />
      );
    } else if (site.amenities.pullThru) {
      amenities.push(
        <AmenityItem key="site-type" icon={MapPin} text="Pull-thru site" />
      );
    } else {
      amenities.push(
        <AmenityItem key="site-type" icon={MapPin} text="Standard site" />
      );
    }

    // Surface type
    if (site.amenities.paved) {
      amenities.push(
        <AmenityItem key="surface" icon={Car} text="Paved site" />
      );
    } else if (site.amenities.gravelSurface) {
      amenities.push(
        <AmenityItem key="surface" icon={Car} text="Gravel surface" />
      );
    } else {
      amenities.push(
        <AmenityItem key="surface" icon={Car} text="Unpaved site" />
      );
    }

    return amenities;
  };

  // Defense in depth: hide anything that isn't active or coming_soon
  // (the query already filters, this guards against stale cached state)
  const status = site.status ?? 'active';
  if (status !== 'active' && status !== 'coming_soon') {
    return null;
  }
  const isComingSoon = status === 'coming_soon';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card className="h-full transition-all duration-200 hover:shadow-lg cursor-pointer">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg font-semibold text-tribal-primary">
                    {site.name}
                  </CardTitle>
                  {/* Always-visible current status indicator (only meaningful when bookable) */}
                  {!isComingSoon && (
                    <div className="flex items-center gap-1">
                      <Circle className={`h-2 w-2 fill-current ${statusDotColor}`} />
                      <span className={`text-xs font-medium ${currentStatusColor}`}>
                        {currentStatus}
                      </span>
                    </div>
                  )}
                </div>
                {/* Date-specific availability badge */}
                {!isComingSoon && startDate && endDate && (
                  <Badge 
                    variant="outline" 
                    className={`${
                      isCheckingAvailability
                        ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse'
                        : isBookedForDateRange
                          ? 'bg-orange-50 text-orange-700 border-orange-200' 
                          : realTimeAvailable === true
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                  >
                    {isCheckingAvailability 
                      ? 'Checking...' 
                      : isBookedForDateRange
                        ? 'Booked for Selected Dates' 
                        : realTimeAvailable === true 
                          ? 'Available for Selected Dates' 
                          : 'Unknown'
                    }
                  </Badge>
                )}
              </div>
              <div className="flex items-center text-sm text-tribal-neutral">
                <MapPin className="h-4 w-4 mr-1" />
                {site.location}
              </div>
            </CardHeader>

            <CardContent className="pb-4">
              <div className="aspect-video mb-4 bg-gray-200 rounded-lg overflow-hidden relative">
                <img 
                  src={site.image} 
                  alt={site.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/lovable-uploads/3bdf8f99-6c1c-425a-9c15-6cc49b6cc262.png';
                  }}
                />
                {isComingSoon && (
                  <div className="absolute top-2 left-2">
                    <Badge className="bg-amber-500 hover:bg-amber-500 text-white shadow-md">
                      Coming Soon
                    </Badge>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-tribal-neutral">
                  <Users className="h-4 w-4 mr-2" />
                  Up to {site.capacity} guests
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  {renderAmenities()}
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-2">
              <div className="flex justify-between items-center w-full">
                <span className="text-2xl font-bold text-tribal-primary">
                  ${site.price}/night
                </span>
              </div>
              {isComingSoon ? (
                <>
                  {site.statusMessage && (
                    <div className="w-full text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      {site.statusMessage}
                    </div>
                  )}
                  <Button
                    disabled
                    className="w-full bg-gray-400 text-white cursor-not-allowed"
                  >
                    Coming Soon
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-full text-sm text-gray-600 mb-2">
                    Book Site to check full availability
                  </div>
                  <Button 
                    onClick={() => onBook(site.id)}
                    disabled={isCheckingAvailability}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    {isCheckingAvailability ? 'Checking...' : 'Book Site'}
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs p-3">
          <BookingTooltip 
            bookings={bookings} 
            siteName={site.name}
            isAvailable={displayAvailable}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CampSiteCard;
